import React, { useContext } from 'react';

// Form primitives mirroring the react-bootstrap composition the app uses:
//   <FormGroup controlId><ControlLabel/><FormControl/><HelpBlock/></FormGroup>
// controlId wires the label's htmlFor to the control's id via context,
// matching react-bootstrap 0.33 behavior.

const FormGroupContext = React.createContext({ controlId: undefined });

export const FormGroup = ({ controlId, className, children, ...rest }) => (
  <FormGroupContext.Provider value={{ controlId }}>
    <div className={['ui-form-group', className].filter(Boolean).join(' ')} {...rest}>
      {children}
    </div>
  </FormGroupContext.Provider>
);

export const ControlLabel = ({ className, htmlFor, children, ...rest }) => {
  const { controlId } = useContext(FormGroupContext);
  return (
    <label
      className={['ui-control-label', className].filter(Boolean).join(' ')}
      htmlFor={htmlFor || controlId}
      {...rest}
    >
      {children}
    </label>
  );
};

/**
 * @param {object} props
 * @param {'input'|'textarea'|'select'|string} [props.componentClass='input']
 *   Element (or component) to render.
 * @param {string} [props.type='text'] Input type when componentClass is input.
 * @param {string} [props.id] Defaults to the enclosing FormGroup's controlId.
 */
export const FormControl = React.forwardRef(function FormControl(
  { componentClass = 'input', type = 'text', id, className, ...rest },
  ref
) {
  const { controlId } = useContext(FormGroupContext);
  const classes = ['ui-form-control', className].filter(Boolean).join(' ');
  const Component = componentClass;

  if (componentClass === 'input') {
    return <input ref={ref} id={id || controlId} type={type} className={classes} {...rest} />;
  }
  return <Component ref={ref} id={id || controlId} className={classes} {...rest} />;
});

export const HelpBlock = ({ className, children, ...rest }) => (
  <span className={['ui-help-block', className].filter(Boolean).join(' ')} {...rest}>
    {children}
  </span>
);

export const InputGroup = ({ className, children, ...rest }) => (
  <div className={['ui-input-group', className].filter(Boolean).join(' ')} {...rest}>
    {children}
  </div>
);

const InputGroupButton = ({ className, children, ...rest }) => (
  <span className={['ui-input-group__btn', className].filter(Boolean).join(' ')} {...rest}>
    {children}
  </span>
);

InputGroup.Button = InputGroupButton;

export const Checkbox = ({ className, checked, onChange, disabled, inline, children, ...rest }) => (
  <div className={['ui-checkbox', className].filter(Boolean).join(' ')}>
    <label>
      <input type="checkbox" checked={checked} onChange={onChange} disabled={disabled} {...rest} />
      {children}
    </label>
  </div>
);

export const Radio = ({ className, checked, onChange, disabled, name, children, ...rest }) => (
  <div className={['ui-radio', className].filter(Boolean).join(' ')}>
    <label>
      <input type="radio" name={name} checked={checked} onChange={onChange} disabled={disabled} {...rest} />
      {children}
    </label>
  </div>
);
